/**
 * Keyboard input. Thin wiring over `InputController`, same as `touch-controls.ts`.
 *
 * `docs/KNOWN-BUGS.md` item 3 notes keyboards hide the release bug entirely,
 * because `keyup` always exists — there is no browser quirk to guard against
 * here the way there is for pointer capture. What still matters is guarding
 * against the OS's own key auto-repeat: held keys fire repeated `keydown`
 * events with no `keyup` between them, and without a check this would
 * re-trigger `press()`'s single-fire actions (rotate, hard drop, hold) on
 * every one of them. `InputController.press` already no-ops a second press
 * while an action is held, so this module only has to avoid calling it twice
 * for the same physical key-down, which the browser does for free — the
 * event object does not distinguish an OS repeat from a fresh press at this
 * layer, so we rely on the controller's own idempotence instead of an
 * `event.repeat` check, which some environments do not set reliably.
 *
 * ## Which key presses are the game's
 *
 * The handler listens on `window`, so it sees every key press on the page —
 * including ones meant for something else. Three kinds are left alone, with
 * no `preventDefault` and no action (`isGameKeyEvent`):
 *
 * - **a press aimed at a control**: an `<input>`, `<textarea>`, `<select>`,
 *   `<button>` or `[contenteditable]`. Space on a focused button has to
 *   click it, and the arrows on a volume slider have to move it;
 * - **a press inside a dialog** (`<dialog>`, `role="dialog"`,
 *   `role="alertdialog"`): a sheet is open over the game, and the keys are
 *   the sheet's;
 * - **a combination with Ctrl, Meta or Alt held**: Ctrl+C is copy, not hold.
 *   A bare modifier still plays — `ControlLeft` alone is rotate — because
 *   then the modifier *is* the key; it is only a second key pressed with one
 *   held that is a shortcut.
 *
 * `keyup` is deliberately not filtered: a key released over a button must
 * still release the action it pressed over the board, or it would repeat
 * forever (`docs/KNOWN-BUGS.md` item 3).
 */
import type { ControllerAction, InputController } from './input-controller.js';

/** `docs/DESIGN.md` names the seven actions; these are Interlock's bindings for them. */
export const DEFAULT_KEY_BINDINGS: Readonly<Record<string, ControllerAction>> = {
  ArrowLeft: 'left',
  ArrowRight: 'right',
  ArrowDown: 'softDrop',
  ArrowUp: 'rotateCW',
  KeyX: 'rotateCW',
  KeyZ: 'rotateCCW',
  ControlLeft: 'rotateCCW',
  ControlRight: 'rotateCCW',
  Space: 'hardDrop',
  KeyC: 'hold',
  ShiftLeft: 'hold',
  ShiftRight: 'hold',
};

export interface BindKeyboardOptions {
  readonly controller: InputController;
  readonly onFire?: (actions: readonly ControllerAction[]) => void;
  readonly bindings?: Readonly<Record<string, ControllerAction>>;
  /** Injectable for tests; defaults to `window`. */
  readonly target?: EventTarget;
}

export interface KeyboardHandle {
  destroy(): void;
}

const MODIFIER_CODES = new Set([
  'ControlLeft',
  'ControlRight',
  'MetaLeft',
  'MetaRight',
  'AltLeft',
  'AltRight',
  'OSLeft',
  'OSRight',
]);

const CONTROL_SELECTOR =
  'input, textarea, select, button, [contenteditable]:not([contenteditable="false"])';
const DIALOG_SELECTOR = 'dialog, [role="dialog"], [role="alertdialog"]';

/**
 * Whether a `keydown` is the game's to act on — see this file's header.
 *
 * Exported so the rule is unit-testable on its own, and so a second driver
 * (there is none today) could not quietly disagree with this one.
 */
export function isGameKeyEvent(event: KeyboardEvent): boolean {
  const isModifierItself = MODIFIER_CODES.has(event.code);
  if (!isModifierItself && (event.ctrlKey || event.metaKey || event.altKey)) return false;

  const target = event.target;
  // `closest` exists on every Element; the window, the document and a test's
  // bare `EventTarget` have none, and a press with no element target is
  // nobody else's.
  if (typeof (target as Element | null)?.closest !== 'function') return true;
  const element = target as Element;
  if (element.closest(CONTROL_SELECTOR) !== null) return false;
  if (element.closest(DIALOG_SELECTOR) !== null) return false;
  return true;
}

export function bindKeyboard(options: BindKeyboardOptions): KeyboardHandle {
  const { controller, onFire, bindings = DEFAULT_KEY_BINDINGS } = options;
  const target = options.target ?? window;

  function onKeyDown(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    const action = bindings[keyboardEvent.code];
    if (action === undefined) return;
    if (!isGameKeyEvent(keyboardEvent)) return;
    event.preventDefault();
    const fired = controller.press(action);
    if (fired.length > 0) onFire?.(fired);
  }

  function onKeyUp(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    const action = bindings[keyboardEvent.code];
    if (action === undefined) return;
    controller.release(action);
  }

  function onBlur(): void {
    controller.releaseAll();
  }

  target.addEventListener('keydown', onKeyDown);
  target.addEventListener('keyup', onKeyUp);
  target.addEventListener('blur', onBlur);

  return {
    destroy(): void {
      target.removeEventListener('keydown', onKeyDown);
      target.removeEventListener('keyup', onKeyUp);
      target.removeEventListener('blur', onBlur);
    },
  };
}
